import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Form, InputField } from "@mimber/forms"
import { Button, message } from "@mimber/ui-web"
import { JoinWaitlistDtoSchema, type JoinWaitlistDto } from "@mimber/schema"
import { useJoinWaitlist } from "@mimber/client/mimber-organization"
import type { JoinWaitlistFormProps } from "./JoinWaitlistForm.types"

export const JoinWaitlistForm: React.FunctionComponent<
  JoinWaitlistFormProps
> = ({ onSuccess, defaultEmail, defaultName }) => {
  const form = useForm<JoinWaitlistDto>({
    resolver: zodResolver(JoinWaitlistDtoSchema),
    mode: "onBlur",
    defaultValues: {
      name: defaultName ?? "",
      email: defaultEmail ?? "",
      organizationName: "",
      source: "organization-app",
    },
  })

  const joinWaitlist = useJoinWaitlist({
    onSuccess: () => {
      message.success("You've been added to the waitlist!")
      onSuccess?.()
    },
    onError: (
      error: Error & {
        response?: {
          status?: number
          data?: { meta?: { error?: { message?: string } } }
        }
      }
    ) => {
      const status = error?.response?.status
      if (status === 409) {
        onSuccess?.()
        return
      }
      // The Axios interceptor transforms 429 into a RateLimitError with a friendly message
      message.error(
        error?.response?.data?.meta?.error?.message ??
          error?.message ??
          "Something went wrong. Please try again."
      )
    },
  })

  const handleSubmit = (data: JoinWaitlistDto) => {
    joinWaitlist.mutate(data)
  }

  return (
    <Form form={form} onSubmit={handleSubmit}>
      <InputField
        name="name"
        type="text"
        label="Full Name"
        placeholder="Enter your full name"
        required
      />
      <InputField
        name="email"
        type="email"
        label="Email"
        placeholder="Enter your email"
        required
      />
      <InputField
        name="organizationName"
        type="text"
        label="Organization Name"
        placeholder="Enter your organization name (optional)"
      />
      <Button
        type="primary"
        htmlType="submit"
        loading={joinWaitlist.isPending}
        className="w-full"
      >
        Join Waitlist
      </Button>
    </Form>
  )
}
